<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <h1 class="login-title">Virtual Care</h1>
          <p class="login-subtitle">Nurse Console</p>
        </div>

        <form @submit.prevent="handleLogin" class="login-form">
          <v-text-field
            v-model="username"
            label="Username"
            type="text"
            variant="outlined"
            :error-messages="errorMessage ? [errorMessage] : []"
            required
            autocomplete="username"
          />

          <v-text-field
            v-model="password"
            label="Password"
            type="password"
            variant="outlined"
            :error-messages="errorMessage ? [errorMessage] : []"
            required
            autocomplete="current-password"
          />

          <v-btn
            type="submit"
            color="primary"
            :loading="loading"
            :disabled="!username || !password"
            block
            size="large"
          >
            Log In
          </v-btn>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import type { LoginRequest, LoginResponse } from '@virtual-care/shared'
import { requireEnv } from '@virtual-care/shared'

const router = useRouter()
const username = ref('')
const password = ref('')
const loading = ref(false)
const errorMessage = ref('')

const apiBaseUrl = requireEnv(process.env.VUE_APP_API_BASE_URL, 'VUE_APP_API_BASE_URL')

const handleLogin = async () => {
  loading.value = true
  errorMessage.value = ''

  try {
    const requestBody: LoginRequest = {
      username: username.value,
      password: password.value,
    }

    const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }))
      throw new Error(error.error || 'Invalid username or password')
    }

    const data = (await response.json()) as LoginResponse

    localStorage.setItem('vc_jwt_token', data.access_token)
    localStorage.setItem('vc_username', data.username)
    localStorage.setItem('vc_display_name', data.displayName)
    localStorage.setItem('vc_caregiver_id', data.caregiverId)

    router.push('/')
  } catch (error) {
    console.error('Login failed:', error)
    errorMessage.value = error instanceof Error ? error.message : 'Login failed. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

.login-container {
  width: 100%;
  max-width: 400px;
}

.login-card {
  background: white;
  border-radius: 12px;
  padding: 40px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.login-header {
  text-align: center;
  margin-bottom: 32px;
}

.login-title {
  font-size: 28px;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 8px 0;
}

.login-subtitle {
  font-size: 16px;
  color: #718096;
  margin: 0;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
